import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

// BrowserRouter does not support useBlocker. Guard its navigator and restore
// history traversals before asking, so the editor stays mounted while saving.
export default function useUnsavedNavigation(dirty, requestLeave) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const latest = useRef({ dirty, requestLeave });
  latest.current = { dirty, requestLeave };

  useEffect(() => {
    const originals = { push: navigator.push, replace: navigator.replace };
    let currentIndex = window.history.state?.idx;
    let restoring = false;
    let allowTraversal = false;
    ['push', 'replace'].forEach(method => {
      navigator[method] = (...args) => {
        const proceed = () => {
          originals[method].apply(navigator, args);
          currentIndex = window.history.state?.idx;
        };
        if (latest.current.dirty) latest.current.requestLeave(proceed);
        else proceed();
      };
    });
    const onPop = event => {
      const nextIndex = event.state?.idx;
      if (restoring) {
        event.stopImmediatePropagation();
        restoring = false;
        return;
      }
      if (allowTraversal || !latest.current.dirty) {
        allowTraversal = false;
        currentIndex = nextIndex;
        return;
      }
      if (Number.isInteger(currentIndex) && Number.isInteger(nextIndex) && currentIndex !== nextIndex) {
        event.stopImmediatePropagation();
        const delta = nextIndex - currentIndex;
        restoring = true;
        window.history.go(-delta);
        latest.current.requestLeave(() => {
          allowTraversal = true;
          window.history.go(delta);
        });
      }
    };
    const onUnload = event => {
      if (!latest.current.dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const onLink = event => {
      if (!latest.current.dirty || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (!link || link.download || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, window.location.href);
      // Router links are handled by the navigator; guard ordinary document links.
      if (url.href === window.location.href || (url.pathname === window.location.pathname && url.search === window.location.search && url.hash)) return;
      if (!['http:', 'https:'].includes(url.protocol)) return;
      event.preventDefault();
      event.stopPropagation();
      latest.current.requestLeave(() => {
        latest.current.dirty = false;
        window.location.assign(url.href);
      });
    };
    window.addEventListener('popstate', onPop, true);
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('click', onLink);
    return () => {
      navigator.push = originals.push;
      navigator.replace = originals.replace;
      window.removeEventListener('popstate', onPop, true);
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('click', onLink);
    };
  }, [navigator]);
}
